<template>
  <el-dialog
    :visible.sync="dialogVisible"
    :title="effectiveRecordId ? '编辑版权档案' : '新建版权档案'"
    width="820px"
    :close-on-click-modal="false"
    append-to-body
    @closed="resetDialog">
    <div v-loading="loading" class="rights-record-dialog">
      <el-alert
        title="一份档案可以关联多个资源"
        type="info"
        :closable="false"
        show-icon>
        权利人、许可条款、合同和证明文件只在这里保存一次；每个资源自己的来源页和署名可以在资源页面单独覆盖。
      </el-alert>

      <el-form label-position="top" class="record-form">
        <el-row :gutter="16">
          <el-col :span="16">
            <el-form-item label="档案名称（必填）">
              <el-input
                v-model.trim="record.record_name"
                maxlength="255"
                placeholder="例如：某平台 CC0 音效、授权合同 2026-001" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="使用方式">
              <el-select v-model="record.usage_mode" style="width:100%">
                <el-option
                  v-for="item in options.usage_modes"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="16">
          <el-col :span="8">
            <el-form-item label="档案类型">
              <el-select v-model="record.record_type" style="width:100%">
                <el-option
                  v-for="item in options.record_types"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="版权情况">
              <el-select v-model="record.rights_status" style="width:100%" @change="handleRightsStatusChange">
                <el-option
                  v-for="item in options.rights_statuses"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="许可版本">
              <el-input v-model.trim="record.license_version" maxlength="16" placeholder="例如：1.0、4.0" />
            </el-form-item>
          </el-col>
        </el-row>

        <div v-if="currentRightsGuide" class="rights-guide">
          <strong>{{ currentRightsGuide.label }}</strong>
          <span>{{ currentRightsGuide.description }}</span>
          <div>
            <el-tag v-for="rule in currentRightsGuide.rules" :key="rule" size="mini" effect="plain">
              {{ rule }}
            </el-tag>
          </div>
          <small>建议保留：{{ currentRightsGuide.evidence_hint }}</small>
        </div>

        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="权利人 / 授权方">
              <el-input v-model.trim="record.rights_holder" maxlength="255" placeholder="作者、素材平台或版权公司" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="授权有效期">
              <el-date-picker
                v-model="validRange"
                type="daterange"
                value-format="timestamp"
                start-placeholder="开始日期"
                end-placeholder="结束日期"
                style="width:100%"
                @change="handleValidRangeChange" />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="共享来源或资源集合页">
              <el-input v-model.trim="record.source_url" maxlength="1000" placeholder="整批资源共同的来源页；单条资源页在关联处填写" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="许可或授权说明页">
              <el-input v-model.trim="record.license_url" maxlength="1000" placeholder="许可协议或授权条款页面" />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="16">
          <el-col :span="8">
            <el-form-item label="允许商业使用">
              <el-select
                v-model="record.commercial_use_allowed"
                :disabled="licenseCapabilitiesLocked"
                style="width:100%">
                <el-option label="材料未说明" :value="null" />
                <el-option label="明确允许" :value="1" />
                <el-option label="明确不允许" :value="0" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="允许修改 / 演绎">
              <el-select
                v-model="record.derivative_use_allowed"
                :disabled="licenseCapabilitiesLocked"
                style="width:100%">
                <el-option label="材料未说明" :value="null" />
                <el-option label="明确允许" :value="1" />
                <el-option label="明确不允许" :value="0" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="审核进度">
              <el-select v-model="record.verification_status" style="width:100%">
                <el-option
                  v-for="item in options.verification_statuses"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="16">
          <el-col :span="8">
            <el-form-item label="风险等级">
              <el-select v-model="record.risk_level" style="width:100%">
                <el-option
                  v-for="item in options.risk_levels"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="16">
            <el-form-item label="默认署名文本">
              <el-input
                v-model.trim="record.attribution_template"
                maxlength="1000"
                placeholder="带 BY 的 CC 许可必须填写；单个资源可以覆盖" />
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item label="档案审核说明">
          <el-input
            v-model.trim="record.review_note"
            type="textarea"
            :rows="3"
            maxlength="5000"
            show-word-limit
            placeholder="记录整份档案的判断依据、限制和仍需补充的材料" />
        </el-form-item>

        <div class="evidence-panel">
          <div class="evidence-heading">
            <div>
              <strong>证明文件</strong>
              <p>不需要填写文件路径；服务器上传后自动保存并计算 SHA-256，相同文件只保留一份。</p>
            </div>
            <el-upload
              ref="recordEvidenceUpload"
              action=""
              accept=".pdf,.jpg,.jpeg,.png,.webp"
              :auto-upload="false"
              :show-file-list="false"
              :on-change="handleEvidenceFileChange">
              <el-button size="small" plain type="primary" icon="el-icon-upload2">选择文件</el-button>
            </el-upload>
          </div>

          <el-alert
            class="evidence-help"
            type="info"
            :closable="false"
            show-icon>
            证明文件不是系统生成的，通常来自权利人或合法来源，例如签署的授权书/合同、版权平台购买凭证、订单或发票、权利人邮件确认、CC/CC0 原始发布页的 PDF 或截图、下架通知等。
          </el-alert>

          <div v-if="evidenceFile" class="pending-file">
            <i class="el-icon-document"></i>
            <span>{{ evidenceFile.name }} · {{ formatFileSize(evidenceFile.size) }}</span>
            <el-button type="text" @click="clearEvidenceFile">取消</el-button>
          </div>

          <div v-for="file in evidence" :key="file.id" class="evidence-row">
            <i class="el-icon-document-checked"></i>
            <div>
              <strong>{{ file.original_name }}</strong>
              <small>{{ formatFileSize(file.file_size) }} · SHA-256 {{ shortHash(file.sha256) }}</small>
            </div>
            <el-button type="text" @click="downloadEvidence(file)">下载</el-button>
            <el-button type="text" class="danger-link" @click="removeEvidence(file)">解除</el-button>
          </div>
          <div v-if="!evidence.length && !evidenceFile" class="empty-evidence">
            没有文件也可以保存草稿。CC/CC0 通常先填写原始来源和许可页面。
          </div>
        </div>

        <el-form-item v-if="effectiveRecordId && Number(record.sound_count) > 0" label="本次修改原因（必填）">
          <el-input
            v-model.trim="changeReason"
            maxlength="1000"
            placeholder="该档案已被资源引用，请说明为什么修改共享内容" />
          <small class="impact-tip">保存后会影响 {{ record.sound_count }} 个关联资源。</small>
        </el-form-item>
      </el-form>
    </div>

    <span slot="footer">
      <el-button @click="dialogVisible = false">取消</el-button>
      <el-button type="primary" :loading="saving" @click="save">保存版权档案</el-button>
    </span>
  </el-dialog>
</template>

<script>
import apis from '@/api/index'

export default {
  name: 'SoundRightsRecordDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    recordId: {
      type: [Number, String],
      default: null
    },
    initialUsageMode: {
      type: String,
      default: 'shared'
    }
  },
  /**
   * 创建版权档案弹窗状态。
   *
   * @returns {Object}
   */
  data() {
    return {
      loading: false,
      saving: false,
      options: {
        usage_modes: [],
        record_types: [],
        rights_statuses: [],
        verification_statuses: [],
        risk_levels: []
      },
      record: this.createDefaultRecord(),
      validRange: null,
      evidence: [],
      evidenceFile: null,
      changeReason: ''
    }
  },
  computed: {
    /**
     * 返回当前已保存的版权档案 ID。
     *
     * 新建档案首次保存成功但后续文件上传失败时，record.id 可以继续标识
     * 已创建档案，管理员再次保存不会意外创建重复记录。
     *
     * @returns {Number}
     */
    effectiveRecordId() {
      return Number(this.recordId || this.record.id || 0)
    },

    /**
     * 把父组件 visible 映射为可双向关闭的弹窗状态。
     *
     * @returns {Boolean}
     */
    dialogVisible: {
      get() {
        return this.visible
      },
      set(value) {
        this.$emit('update:visible', value)
      }
    },

    /**
     * 返回当前版权情况的通俗解释。
     *
     * @returns {Object|null}
     */
    currentRightsGuide() {
      return (this.options.rights_statuses || []).find(
        item => item.value === this.record.rights_status
      ) || null
    },

    /**
     * 判断商用和演绎能力是否由标准 CC 许可固定。
     *
     * @returns {Boolean}
     */
    licenseCapabilitiesLocked() {
      return ['cc0', 'cc_by', 'cc_by_sa', 'cc_by_nd', 'cc_by_nc', 'cc_by_nc_sa', 'cc_by_nc_nd']
        .includes(this.record.rights_status)
    }
  },
  watch: {
    /**
     * 打开弹窗时加载选项和目标档案。
     *
     * @param {Boolean} value 最新可见状态
     * @returns {void}
     */
    visible(value) {
      if (value) this.openDialog()
    }
  },
  methods: {
    /**
     * 创建统一版权档案默认值。
     *
     * @returns {Object}
     */
    createDefaultRecord() {
      return {
        record_name: '',
        usage_mode: this.initialUsageMode || 'shared',
        record_type: 'other',
        rights_status: 'unknown',
        license_version: '',
        rights_holder: '',
        source_url: '',
        license_url: '',
        attribution_template: '',
        commercial_use_allowed: null,
        derivative_use_allowed: null,
        verification_status: 'unreviewed',
        risk_level: 'unknown',
        valid_from: null,
        valid_until: null,
        review_note: '',
        sound_count: 0
      }
    },

    /**
     * 初始化弹窗并读取已有档案。
     *
     * @returns {Promise<void>}
     */
    async openDialog() {
      this.loading = true
      try {
        const optionResponse = await apis.soundRightsRecord.options()
        if (optionResponse.data.code !== 1) {
          throw new Error(optionResponse.data.msg || '版权档案选项加载失败')
        }
        this.options = { ...this.options, ...(optionResponse.data.data || {}) }
        if (Number(this.recordId || 0) > 0) {
          const response = await apis.soundRightsRecord.read({ record_id: Number(this.recordId) })
          if (response.data.code !== 1) throw new Error(response.data.msg || '版权档案加载失败')
          const loaded = (response.data.data && response.data.data.record) || {}
          this.record = { ...this.createDefaultRecord(), ...loaded }
          this.evidence = loaded.evidence || []
          this.validRange = loaded.valid_from && loaded.valid_until
            ? [Number(loaded.valid_from) * 1000, Number(loaded.valid_until) * 1000]
            : null
        } else {
          this.record = this.createDefaultRecord()
          this.record.usage_mode = this.initialUsageMode || 'shared'
        }
      } catch (error) {
        this.$message.error(error.message || String(error))
      } finally {
        this.loading = false
      }
    },

    /**
     * 重置已经关闭的弹窗，防止下次显示旧文件或旧修改原因。
     *
     * @returns {void}
     */
    resetDialog() {
      this.record = this.createDefaultRecord()
      this.validRange = null
      this.evidence = []
      this.evidenceFile = null
      this.changeReason = ''
    },

    /**
     * 根据标准 CC 许可即时同步固定能力。
     *
     * @param {String} rightsStatus 版权情况编码
     * @returns {void}
     */
    handleRightsStatusChange(rightsStatus) {
      const capabilities = {
        cc0: [1, 1],
        cc_by: [1, 1],
        cc_by_sa: [1, 1],
        cc_by_nd: [1, 0],
        cc_by_nc: [0, 1],
        cc_by_nc_sa: [0, 1],
        cc_by_nc_nd: [0, 0]
      }
      if (capabilities[rightsStatus]) {
        this.record.commercial_use_allowed = capabilities[rightsStatus][0]
        this.record.derivative_use_allowed = capabilities[rightsStatus][1]
      }
    },

    /**
     * 把日期区间同步为服务端使用的秒级时间戳。
     *
     * @param {Array|null} range 毫秒时间戳区间
     * @returns {void}
     */
    handleValidRangeChange(range) {
      this.record.valid_from = range && range[0] ? Math.floor(Number(range[0]) / 1000) : null
      this.record.valid_until = range && range[1] ? Math.floor(Number(range[1]) / 1000) : null
    },

    /**
     * 接收并快速校验待上传证明文件。
     *
     * @param {Object} file Element UI 文件对象
     * @returns {void}
     */
    handleEvidenceFileChange(file) {
      const rawFile = file && file.raw ? file.raw : null
      if (!rawFile || !/\.(pdf|jpe?g|png|webp)$/i.test(rawFile.name || '')) {
        this.$message.warning('只支持 PDF、JPG、PNG 或 WebP 证明文件')
        this.clearEvidenceFile()
        return
      }
      if (!rawFile.size || rawFile.size > 10 * 1024 * 1024) {
        this.$message.warning('证明文件大小必须在 1 字节到 10 MB 之间')
        this.clearEvidenceFile()
        return
      }
      this.evidenceFile = rawFile
    },

    /**
     * 清除尚未上传的本地文件。
     *
     * @returns {void}
     */
    clearEvidenceFile() {
      this.evidenceFile = null
      if (this.$refs.recordEvidenceUpload) this.$refs.recordEvidenceUpload.clearFiles()
    },

    /**
     * 保存版权档案并在需要时上传证明文件。
     *
     * 新档案若仅依赖待上传文件完成核验，会先以待补材料状态创建，文件
     * 关联成功后再保存最终审核状态，避免产生“已核验但没有依据”的瞬间。
     *
     * @returns {Promise<void>}
     */
    async save() {
      if (!String(this.record.record_name || '').trim()) {
        this.$message.warning('请填写版权档案名称')
        return
      }
      if (this.effectiveRecordId && Number(this.record.sound_count) > 0 && !this.changeReason) {
        this.$message.warning(`该档案已被 ${this.record.sound_count} 个资源引用，请填写修改原因`)
        return
      }

      this.saving = true
      try {
        let targetId = this.effectiveRecordId
        const desiredVerification = this.record.verification_status
        const needsStagedCreate = !targetId &&
          this.evidenceFile &&
          desiredVerification === 'verified' &&
          !this.record.source_url &&
          !this.record.license_url
        const firstRecord = needsStagedCreate
          ? { ...this.record, verification_status: 'pending' }
          : this.record
        let response = await apis.soundRightsRecord.save({
          record_id: targetId,
          record: firstRecord,
          change_reason: this.changeReason
        })
        if (response.data.code !== 1) throw new Error(response.data.msg || '版权档案保存失败')
        let savedRecord = response.data.data.record
        targetId = Number(savedRecord.id)
        this.record = { ...this.record, ...savedRecord }

        if (this.evidenceFile) {
          const formData = new FormData()
          formData.append('record_id', targetId)
          formData.append('evidence_file', this.evidenceFile)
          response = await apis.soundRightsRecord.uploadEvidence(formData)
          if (response.data.code !== 1) throw new Error(response.data.msg || '证明文件上传失败')
          savedRecord = response.data.data.record
          this.clearEvidenceFile()
        }

        if (needsStagedCreate) {
          response = await apis.soundRightsRecord.save({
            record_id: targetId,
            record: { ...savedRecord, verification_status: desiredVerification },
            change_reason: ''
          })
          if (response.data.code !== 1) throw new Error(response.data.msg || '最终审核状态保存失败')
          savedRecord = response.data.data.record
        }

        this.$message.success('版权档案已保存')
        this.$emit('saved', savedRecord)
        this.dialogVisible = false
      } catch (error) {
        this.$message.error(error.message || String(error))
      } finally {
        this.saving = false
      }
    },

    /**
     * 下载档案中的证明文件。
     *
     * @param {Object} file 证明文件元数据
     * @returns {Promise<void>}
     */
    async downloadEvidence(file) {
      try {
        const response = await apis.soundRightsRecord.downloadEvidence({
          record_id: this.effectiveRecordId,
          evidence_id: Number(file.id)
        })
        const contentType = String((response.headers && response.headers['content-type']) || '')
        if (contentType.includes('application/json')) {
          const text = await this.readBlobAsText(response.data)
          const data = JSON.parse(text || '{}')
          throw new Error(data.msg || '证明文件下载失败')
        }
        const url = window.URL.createObjectURL(response.data)
        const link = document.createElement('a')
        link.href = url
        link.download = file.original_name || '版权证明'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (error) {
        this.$message.error(error.message || String(error))
      }
    },

    /**
     * 解除当前档案与证明文件的关联。
     *
     * @param {Object} file 证明文件元数据
     * @returns {Promise<void>}
     */
    async removeEvidence(file) {
      try {
        await this.$confirm(
          '确认从当前版权档案解除这份证明文件吗？若其他档案仍在使用，系统不会删除物理文件。',
          '解除证明文件',
          { type: 'warning', confirmButtonText: '确认解除', cancelButtonText: '取消' }
        )
      } catch (error) {
        return
      }
      try {
        const response = await apis.soundRightsRecord.removeEvidence({
          record_id: this.effectiveRecordId,
          evidence_id: Number(file.id)
        })
        if (response.data.code !== 1) throw new Error(response.data.msg || '解除失败')
        const saved = response.data.data.record
        this.record = { ...this.record, ...saved }
        this.evidence = saved.evidence || []
        this.$message.success(response.data.msg || '已解除')
      } catch (error) {
        this.$message.error(error.message || String(error))
      }
    },

    /**
     * 把 Blob 错误响应还原为文本。
     *
     * @param {Blob} blob 错误响应
     * @returns {Promise<String>}
     */
    readBlobAsText(blob) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(String(reader.result || ''))
        reader.onerror = () => reject(new Error('下载错误信息读取失败'))
        reader.readAsText(blob, 'UTF-8')
      })
    },

    /**
     * 格式化文件大小。
     *
     * @param {Number|String} bytes 字节数
     * @returns {String}
     */
    formatFileSize(bytes) {
      const size = Number(bytes || 0)
      if (size <= 0) return '大小未知'
      if (size < 1024) return `${size} B`
      if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`
      return `${(size / 1024 / 1024).toFixed(1)} MB`
    },

    /**
     * 缩短页面展示的 SHA-256。
     *
     * @param {String} hash 完整哈希
     * @returns {String}
     */
    shortHash(hash) {
      const value = String(hash || '')
      return value.length > 20 ? `${value.slice(0, 10)}…${value.slice(-10)}` : value
    }
  }
}
</script>

<style lang="scss" scoped>
.rights-record-dialog {
  max-height: 68vh;
  padding-right: 4px;
  overflow-y: auto;
}

.record-form {
  margin-top: 16px;
}

.rights-guide {
  display: flex;
  margin: -4px 0 16px;
  padding: 12px 14px;
  border-radius: 7px;
  background: #f5faff;
  flex-direction: column;
  gap: 7px;
  color: #606266;
  line-height: 1.5;

  div {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  small {
    color: #7a8699;
  }
}

.evidence-panel {
  margin-bottom: 18px;
  padding: 14px;
  border: 1px solid #dcdfe6;
  border-radius: 8px;
  background: #fafbfc;
}

.evidence-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;

  p {
    margin: 5px 0 0;
    color: #909399;
    font-size: 12px;
  }
}

.pending-file,
.evidence-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
  padding: 10px 12px;
  border-radius: 6px;
  background: #fff;
}

.evidence-help {
  margin-top: 12px;
}

.pending-file {
  span {
    flex: 1;
  }
}

.evidence-row {
  > div {
    display: flex;
    flex: 1;
    min-width: 0;
    flex-direction: column;
    gap: 4px;
  }

  strong {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  small {
    color: #909399;
  }
}

.empty-evidence {
  margin-top: 10px;
  color: #909399;
  font-size: 12px;
}

.danger-link {
  color: #f56c6c;
}

.impact-tip {
  color: #e6a23c;
}
</style>
